import {Action, AlertProps, DataStatus, Parameter} from 'platform/components';
import {Box, CSSDimension, Integer, Show, ThemeIconKey, VStack} from 'platform/foundation';

import {ReactNode} from 'react';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {EntityResourceIds} from '@dms/api/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {EntityHeaderControl} from '../EntityHeader/components/EntityHeaderControls';
import {SnippetProps} from '../EntityHeader/components/Snippet';
import {
  EntityHeader,
  EntityHeaderDropdownFlag,
  EntityHeaderFlag,
} from '../EntityHeader/EntityHeader';
import {HeaderPermissions} from '../EntityHeader/types/headerPermissions';
import {NavigationItem, Page} from '../Page/Page';
import {SectionItem, Sections} from '../Sections/Sections';
import {ControlledSection} from '../Sections/types/ControlledSection';

export type DetailTemplateHeader = {
  title: string;
  height?: CSSDimension | Integer;
  icon?: ThemeIconKey;
  image?: string;
  parameters?: Parameter[];
  flags?: EntityHeaderFlag[];
  dropdownFlags?: EntityHeaderDropdownFlag[];
  secondaryParameter?: string;
  primaryParameter?: string;
  snippets?: SnippetProps[];
  actions?: Action[];
  resourceId?: EntityResourceIds;
  recordId?: string;
  onImageClick?: () => void;
  controls?: EntityHeaderControl[];
  alerts?: AlertProps[];
  parkingRecordId?: string;
  authorizationProfileRecordId?: string;
  isAuthorizationProfileEditable?: boolean;
  onAuthorizationProfileChange?: () => void;
  isEntityLoading?: boolean;
  imageRatio?: `${number}/${number}`;
  enableTeams?: boolean;
};

type NavigationOrSection =
  | {
      sections?: never;
      navigation: NavigationItem[];
    }
  | {
      sections: SectionItem[];
      navigation?: never;
    };

type DetailTemplateProps = {
  isLoading: boolean;
  isError: boolean;
  header?: DetailTemplateHeader;
  defaultSection?: string;
  permissions?: HeaderPermissions;
  isLocked?: boolean;
  isFullHeight?: boolean;
  activityPanel?: ReactNode;
} & NavigationOrSection &
  RequiredTestIdProps &
  ControlledSection;

export function DetailTemplate(props: DetailTemplateProps) {
  const entityHeader = (
    <EntityHeader
      title={props.header?.title}
      parameters={props.header?.parameters}
      image={props.header?.image}
      icon={props.header?.icon}
      flags={props.header?.flags}
      dropdownFlags={props.header?.dropdownFlags}
      primaryParameter={props.header?.primaryParameter}
      secondaryParameter={props.header?.secondaryParameter}
      snippets={props.header?.snippets}
      actions={props.header?.actions}
      recordId={props.header?.recordId}
      resourceId={props.header?.resourceId}
      onImageClick={props.header?.onImageClick}
      controls={props.header?.controls}
      data-testid={suffixTestId('detail', props)}
      permissions={props.permissions}
      alerts={props.header?.alerts}
      imageRatio={props.header?.imageRatio}
      parkingRecordId={props.header?.parkingRecordId}
      authorizationProfileRecordId={props.header?.authorizationProfileRecordId}
      isAuthorizationProfileEditable={props.header?.isAuthorizationProfileEditable}
      onAuthorizationProfileChange={props.header?.onAuthorizationProfileChange}
      isEntityLoading={props.header?.isEntityLoading}
      enableTeams={props.header?.enableTeams}
    />
  );

  const sections = (
    <Sections
      isFullHeight={props.isFullHeight}
      sections={props.sections as SectionItem[]}
      defaultSection={props.defaultSection}
      data-testid={suffixTestId('detail', props)}
      activeSectionId={props.activeSectionId}
      onSectionChange={props.onSectionChange}
      activityPanel={props.activityPanel}
      isLocked={props.isLocked}
    />
  );

  return (
    <>
      <DataStatus
        isLoading={!props.header || props.isLoading}
        isError={props.isError}
        minHeight={props.header?.height ?? 61}
      >
        <Show when={isNotNilOrEmpty(props.sections)}>
          {props.isFullHeight ? (
            <VStack align="stretch" justify="stretch" height="100%">
              {entityHeader}
              <Box flex={1}>{sections}</Box>
            </VStack>
          ) : (
            <>
              {entityHeader}
              {sections}
            </>
          )}
        </Show>
        <Show when={isNotNilOrEmpty(props.navigation)}>
          <Page
            isFullHeight
            navigation={props.navigation}
            data-testid={suffixTestId('detail', props)}
            isLocked={props.isLocked}
            activityPanel={props.activityPanel}
            header={
              <EntityHeader
                title={props.header?.title}
                parameters={props.header?.parameters}
                image={props.header?.image}
                icon={props.header?.icon}
                flags={props.header?.flags}
                primaryParameter={props.header?.primaryParameter}
                secondaryParameter={props.header?.secondaryParameter}
                snippets={props.header?.snippets}
                actions={props.header?.actions}
                recordId={props.header?.recordId}
                resourceId={props.header?.resourceId}
                onImageClick={props.header?.onImageClick}
                controls={props.header?.controls}
                data-testid={suffixTestId('detail', props)}
                permissions={props.permissions}
                alerts={props.header?.alerts}
                authorizationProfileRecordId={props.header?.authorizationProfileRecordId}
                isAuthorizationProfileEditable={props.header?.isAuthorizationProfileEditable}
                onAuthorizationProfileChange={props.header?.onAuthorizationProfileChange}
                isEntityLoading={props.header?.isEntityLoading}
                enableTeams={props.header?.enableTeams}
              />
            }
          />
        </Show>
      </DataStatus>
    </>
  );
}
